var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import * as React from "react";
import { Button, Dimmer, Header, List, Loader, Message, Modal, Table } from "semantic-ui-react";
import { downloadBlob } from "./download";
import { postJsonWithAuthn, postWithAuthn } from "./RequestHelpers";
var TEMPLATE_HEADER = 'Tax Id,Target,Sequence,System,Comment';
var EXAMPLE_ROWS = [
    ['9606', 'GAPDH', 'AUGGGGAAGGUGAAGGUCGGAGUCAACGGAUUUGGUCGUAUUGGG', 'X3', 'replicate 1'],
    ['7955', 'shha', 'ACGGGUUUCAACUGACAGCUUCAUUGGAAGACUCACCCUAGGCUU', 'X4', ''],
];
/**
 * Bulk ordering from a CSV: downloads a template, uploads the filled file for server-side
 * validation, shows a per-row confirmation with errors, and adds one RNA-FISH kit per valid row
 * to the cart.
 */
var BulkOrderModal = /** @class */ (function (_super) {
    __extends(BulkOrderModal, _super);
    function BulkOrderModal(props) {
        var _this = _super.call(this, props) || this;
        _this.fileInputRef = React.createRef();
        _this.renderIntro = function () {
            return React.createElement(React.Fragment, null,
                React.createElement("p", null, "Fill in one row per kit using the CSV template, then upload the file. Each row becomes one RNA-FISH kit with the HCR\u2122 system you specify. Kits are added with our standard defaults and can be edited individually in the cart afterward."),
                React.createElement(Button, { icon: 'download', content: 'Download CSV template', labelPosition: 'left', onClick: _this.downloadTemplate }),
                React.createElement(Header, { size: 'small' }, "Example"),
                React.createElement("div", { style: { overflowX: 'auto' } },
                    React.createElement(Table, { celled: true, compact: true, size: 'small' },
                        React.createElement(Table.Header, null,
                            React.createElement(Table.Row, null, TEMPLATE_HEADER.split(',').map(function (h) {
                                return React.createElement(Table.HeaderCell, { key: h }, h);
                            }))),
                        React.createElement(Table.Body, null, EXAMPLE_ROWS.map(function (row, idx) {
                            return React.createElement(Table.Row, { key: idx }, row.map(function (cell, cellIdx) {
                                return React.createElement(Table.Cell, { key: cellIdx }, _this.abbreviate(cell));
                            }));
                        })))),
                React.createElement(List, { bulleted: true, style: { color: 'grey', fontSize: '0.9em' } },
                    React.createElement(List.Item, null, "Tax ID: the NCBI taxonomy id of the organism (e.g. 9606 for human)"),
                    React.createElement(List.Item, null, "Target: the name of the RNA target"),
                    React.createElement(List.Item, null, "Sequence: the RNA target sequence, at least 15 nt (A/C/G/T/U)"),
                    React.createElement(List.Item, null, "System: the HCR\u2122 amplifier, e.g. X3 or X4"),
                    React.createElement(List.Item, null, "Comment: optional, added to the kit")),
                _this.state.fileErrors.length > 0 &&
                    React.createElement(Message, { error: true },
                        React.createElement(List, null, _this.state.fileErrors.map(function (e, idx) { return React.createElement(List.Item, { key: idx }, e.msg); }))),
                React.createElement("input", { type: 'file', accept: '.csv', ref: _this.fileInputRef, style: { display: 'none' }, onChange: _this.onFileSelected }),
                React.createElement(Button, { icon: 'upload', content: 'Upload filled CSV', labelPosition: 'left', positive: true, onClick: _this.pickFile }));
        };
        _this.renderPreview = function (preview) {
            var errorCount = preview.rows.filter(function (r) { return r.errors.length > 0; }).length;
            return React.createElement(React.Fragment, null,
                preview.valid
                    ? React.createElement(Message, { positive: true }, preview.rows.length === 1
                        ? 'Your row looks good. Review the kit below, then confirm to add it to your cart.'
                        : "All " + preview.rows.length + " rows look good. Review the kits below, then confirm to add them to your cart.")
                    : React.createElement(Message, { warning: true }, preview.rows.length === 1
                        ? 'Your row has a problem. Fix it in your CSV and upload it again.'
                        : errorCount === 1
                            ? "1 of " + preview.rows.length + " rows has a problem. Fix it in your CSV and upload it again."
                            : errorCount + " of " + preview.rows.length + " rows have problems. Fix them in your CSV and upload it again."),
                React.createElement("div", { style: { maxHeight: '400px', overflow: 'auto' } },
                    React.createElement(Table, { celled: true, compact: true, size: 'small' },
                        React.createElement(Table.Header, null,
                            React.createElement(Table.Row, null,
                                React.createElement(Table.HeaderCell, null, "Kit #"),
                                React.createElement(Table.HeaderCell, null, "Tax Id"),
                                React.createElement(Table.HeaderCell, null, "Organism"),
                                React.createElement(Table.HeaderCell, null, "Target"),
                                React.createElement(Table.HeaderCell, null, "Sequence"),
                                React.createElement(Table.HeaderCell, null, "System"),
                                React.createElement(Table.HeaderCell, null, "Comment"))),
                        React.createElement(Table.Body, null, preview.rows.map(function (r) { return _this.renderPreviewRow(r); })))),
                React.createElement("div", { style: { marginTop: '1em' } },
                    React.createElement(Button, { content: 'Choose a different file', onClick: _this.reset }),
                    React.createElement(Button, { icon: 'cart', content: preview.rows.length === 1 ? 'Add 1 kit to cart' : "Add " + preview.rows.length + " kits to cart", labelPosition: 'left', positive: true, disabled: !preview.valid, onClick: _this.confirm })));
        };
        _this.renderPreviewRow = function (row) {
            var hasErrors = row.errors.length > 0;
            // error paths name the CSV column the error belongs to, e.g. "/taxId"; only those cells
            // get the red treatment — the rest of the row (and the message row below) stay plain
            var errorPaths = new Set(row.errors.map(function (e) { return e.path; }));
            var cellStyle = function (path) {
                return errorPaths.has(path) ? { backgroundColor: '#fff6f6', color: '#9f3a38' } : undefined;
            };
            var organism = row.organismName !== null
                ? "" + row.organismName + (row.organismScientificName ? " (" + row.organismScientificName + ")" : '')
                : undefined;
            return React.createElement(React.Fragment, { key: row.rowNumber },
                React.createElement(Table.Row, null,
                    React.createElement(Table.Cell, null, row.rowNumber),
                    React.createElement(Table.Cell, { style: cellStyle('/taxId') }, row.row.taxId),
                    React.createElement(Table.Cell, { style: cellStyle('/taxId') }, organism),
                    React.createElement(Table.Cell, { style: cellStyle('/target') }, row.row.target),
                    React.createElement(Table.Cell, { style: cellStyle('/sequence') }, _this.abbreviate(row.row.sequence)),
                    React.createElement(Table.Cell, { style: cellStyle('/system') }, row.row.system),
                    React.createElement(Table.Cell, null, _this.abbreviate(row.row.comment))),
                hasErrors &&
                    React.createElement(Table.Row, null,
                        React.createElement(Table.Cell, null),
                        React.createElement(Table.Cell, { colSpan: 6, style: { textAlign: 'left' } },
                            React.createElement(List, null, row.errors.map(function (e, idx) {
                                return React.createElement(List.Item, { key: idx },
                                    React.createElement(List.Icon, { name: 'warning circle', color: 'red', verticalAlign: 'middle' }),
                                    React.createElement(List.Content, { verticalAlign: 'middle' }, e.msg));
                            })))));
        };
        _this.renderSuccess = function () {
            return React.createElement(React.Fragment, null,
                React.createElement(Message, { positive: true },
                    React.createElement(Message.Header, null, _this.state.addedCount === 1 ? 'Kit added' : 'Kits added'),
                    _this.state.addedCount === 1
                        ? '1 kit was added to your cart. You can edit it from the cart.'
                        : _this.state.addedCount + " kits were added to your cart. You can edit any of them individually from the cart."),
                React.createElement(Button, { content: 'Close', positive: true, onClick: _this.close }));
        };
        _this.downloadTemplate = function () {
            // header plus one empty row to start typing into -- the example rows are shown in the
            // modal, not shipped in the file (the backend skips fully blank rows)
            downloadBlob('bulk_order_template.csv', TEMPLATE_HEADER + '\n,,,,\n', 'text/csv;charset=utf-8');
        };
        _this.openModal = function () {
            _this.setState({ open: true });
        };
        _this.openModalOnKeyboard = function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                _this.openModal();
            }
        };
        _this.close = function () {
            var added = _this.state.addedCount !== undefined;
            _this.setState({
                open: false,
                preview: undefined,
                fileErrors: [],
                addedCount: undefined
            }, function () {
                if (added) {
                    _this.props.onKitsAdded();
                }
            });
        };
        _this.reset = function () {
            _this.setState({ preview: undefined, fileErrors: [], addedCount: undefined });
        };
        _this.pickFile = function () {
            var _a;
            (_a = _this.fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click();
        };
        _this.onFileSelected = function (event) {
            var _a;
            var file = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0];
            // allow re-uploading the same (fixed) file
            event.target.value = '';
            if (!file) {
                return;
            }
            _this.setState({ loading: true, fileErrors: [] }, function () {
                var formData = new FormData();
                formData.append('file', file);
                postWithAuthn('/cart/bulk/validate', formData)
                    .then(function (resp) { return resp.json().then(function (json) { return ({ ok: resp.ok, json: json }); }); })
                    .then(function (_a) {
                    var _b;
                    var ok = _a.ok, json = _a.json;
                    if (ok) {
                        _this.setState({ loading: false, preview: json });
                    }
                    else {
                        _this.setState({ loading: false, fileErrors: (_b = json.errors) !== null && _b !== void 0 ? _b : [] });
                    }
                })
                    .catch(function (rej) {
                    console.error("Couldn't validate bulk order CSV: " + rej);
                    _this.setState({
                        loading: false,
                        fileErrors: [{ path: '/file', msg: 'An unexpected error occurred' }]
                    });
                });
            });
        };
        _this.confirm = function () {
            var preview = _this.state.preview;
            if (preview === undefined || !preview.valid) {
                return;
            }
            _this.setState({ loading: true }, function () {
                postJsonWithAuthn('/cart/bulk/confirm', JSON.stringify({ rows: preview.rows.map(function (r) { return r.row; }) }))
                    .then(function (resp) { return resp.json().then(function (json) { return ({ ok: resp.ok, json: json }); }); })
                    .then(function (_a) {
                    var _b;
                    var ok = _a.ok, json = _a.json;
                    if (ok) {
                        _this.setState({ loading: false, addedCount: json.added });
                    }
                    else if (json.rows !== undefined) {
                        // something changed server-side since validation; re-show per-row errors
                        _this.setState({ loading: false, preview: json });
                    }
                    else {
                        _this.setState({
                            loading: false,
                            preview: undefined,
                            fileErrors: (_b = json.errors) !== null && _b !== void 0 ? _b : [{ path: '/file', msg: 'An unexpected error occurred' }]
                        });
                    }
                })
                    .catch(function (rej) {
                    console.error("Couldn't confirm bulk order: " + rej);
                    _this.setState({
                        loading: false,
                        fileErrors: [{ path: '/file', msg: 'An unexpected error occurred' }]
                    });
                });
            });
        };
        _this.abbreviate = function (seq) {
            if (seq.length <= 24) {
                return seq;
            }
            return seq.substring(0, 21) + '...';
        };
        _this.state = {
            open: false,
            loading: false,
            preview: undefined,
            fileErrors: [],
            addedCount: undefined
        };
        return _this;
    }
    BulkOrderModal.prototype.render = function () {
        var _a;
        return React.createElement(Modal, { closeIcon: true, open: this.state.open, onClose: this.close, trigger: 
            // Styled to sit among the cart's "Add kit:" links rather than as a button
            React.createElement("a", { className: 'add-kit-link', role: 'button', tabIndex: 0, style: { cursor: 'pointer' }, onClick: this.openModal, onKeyDown: this.openModalOnKeyboard }, (_a = this.props.triggerLabel) !== null && _a !== void 0 ? _a : 'Bulk HCR™ Probe Order') },
            React.createElement(Modal.Header, null, "Bulk Order HCR\u2122 Probes"),
            React.createElement(Modal.Content, null,
                React.createElement(Dimmer.Dimmable, { as: 'div' },
                    React.createElement(Dimmer, { active: this.state.loading, inverted: true },
                        React.createElement(Loader, null)),
                    this.state.addedCount !== undefined
                        ? this.renderSuccess()
                        : this.state.preview !== undefined
                            ? this.renderPreview(this.state.preview)
                            : this.renderIntro())));
    };
    return BulkOrderModal;
}(React.PureComponent));
export default BulkOrderModal;
//# sourceMappingURL=BulkOrderModal.js.map


//////////////////
// WEBPACK FOOTER
// ../common/build/dist/BulkOrderModal.js
// module id = 1153
// module chunks = 0